import {
  Box,
  Fade,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  AlertTriangle,
  ChevronDown,
  HelpCircle,
  KeyRound,
  UserMinus,
} from "lucide-react";
import { memo } from "react";
import { Avatar, Button } from "components/ds";
import { getButtonStyle, getButtonTone } from "./AccountRow";
import { selectableOutcomeOptions } from "../reviewLogic";
import type { MFReviewAccount } from "state/members/accessReviews";

const accountAvatarName = (account: MFReviewAccount): string =>
  account.member.name?.trim() ||
  account.member.email ||
  account.identity?.displayName ||
  account.identity?.extId ||
  "Unnamed";

const FloatingActionBar = memo(
  ({
    selectedCount,
    onAction,
    onChangeRoles,
    onBulkMarkNonExistent,
    onRaiseQuery,
    isVisible,
    isLoading,
    outcomeOptions,
    selectedAccounts,
    entitlementOnly = false,
  }: {
    selectedCount: number;
    onAction: (action: string) => void;
    /** Open the bulk "correct entitlements" (tri-state) modal. */
    onChangeRoles?: () => void;
    /** Open the bulk "mark as non existing" confirmation. */
    onBulkMarkNonExistent?: () => void;
    /** Raise a query for the whole selection (opens the mandatory-note modal). */
    onRaiseQuery?: () => void;
    isVisible: boolean;
    isLoading: boolean;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
    selectedAccounts?: MFReviewAccount[];
    // Entitlement-only cycle (PLO-5731): the selection is entitlements, one per
    // row, so the flag-for-adjustment outcome has nothing to pick out of them.
    entitlementOnly?: boolean;
  }) => {
    const actionOptions = selectableOutcomeOptions(
      outcomeOptions,
      entitlementOnly,
    );
    return (
      <Fade in={isVisible} unmountOnExit>
        <Box
          position="fixed"
          bottom="32px"
          left="50%"
          transform="translateX(-50%)"
          width="calc(100% - 32px)"
          maxW="720px"
          minWidth={0}
          zIndex={10}
          bg="bg.container"
          boxShadow="elevHigh"
          borderRadius="component.2xl"
          py="md"
          px="lg"
          borderWidth="1px"
          borderColor="border.default"
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            gap="md"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <HStack spacing="sm" minW={0}>
              {/* Avatar previews */}
              {selectedAccounts && selectedAccounts.length > 0 && (
                <Box as="span" display="inline-flex" alignItems="center">
                  {selectedAccounts.slice(0, 3).map((account, index) => (
                    <Box
                      as="span"
                      key={account.id}
                      display="inline-flex"
                      ml={index === 0 ? 0 : "-8px"}
                      zIndex={3 - index}
                    >
                      <Avatar
                        name={accountAvatarName(account)}
                        src={account.member.profilePictureUrl ?? undefined}
                        size="sm"
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <Text
                color="text.body.primary"
                fontFamily="body"
                fontSize="14px"
                fontWeight="500"
                lineHeight="100%"
                whiteSpace="nowrap"
              >
                {selectedCount}{" "}
                {entitlementOnly ? "entitlement" : "account"}
                {selectedCount !== 1 ? "s" : ""} selected
              </Text>
            </HStack>
            <HStack spacing="sm" flexWrap="wrap" justify="flex-end">
              {/* Fix incorrect data — a data-fix menu (NOT a review decision):
                  correct entitlements, or mark accounts as not existing at
                  source. */}
              {(onChangeRoles || onBulkMarkNonExistent) && (
                <Menu placement="top-end" isLazy>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    tone="error"
                    size="md"
                    isDisabled={isLoading}
                    leadingIcon={<Icon as={AlertTriangle} boxSize={4} />}
                    trailingIcon={<Icon as={ChevronDown} boxSize={4} />}
                  >
                    Fix incorrect data
                  </MenuButton>
                  <MenuList
                    minW="248px"
                    py="2xs"
                    border="1px solid"
                    borderColor="border.default"
                    boxShadow="overlay"
                  >
                    {onChangeRoles && (
                      <MenuItem
                        fontSize="smd"
                        py="sm"
                        iconSpacing="sm"
                        icon={<Icon as={KeyRound} boxSize={4} />}
                        onClick={onChangeRoles}
                      >
                        Bulk correct entitlements
                      </MenuItem>
                    )}
                    {onBulkMarkNonExistent && (
                      <MenuItem
                        fontSize="smd"
                        py="sm"
                        iconSpacing="sm"
                        icon={<Icon as={UserMinus} boxSize={4} />}
                        onClick={onBulkMarkNonExistent}
                      >
                        Bulk mark as non existing
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              )}

              {/* Divider before buttons */}
              <Box
                height="32px"
                width="1px"
                bg="border.default"
                display={{ base: "none", md: "block" }}
              />

              {/* Review status buttons */}
              <HStack spacing="xs">
                {actionOptions.map((option) => {
                  const style = getButtonStyle(
                    option.standard_mapping,
                    entitlementOnly,
                  );
                  return (
                    <Tooltip
                      key={option.standard_mapping}
                      label={option.name}
                      hasArrow
                      bg="text.body.primary"
                      color="bg.container"
                      borderColor="border.strong"
                      borderWidth="1px"
                      borderRadius="component.md"
                      px="xs"
                      py="xs"
                      fontSize="14px"
                      fontFamily="body"
                      fontWeight="500"
                      lineHeight="100%"
                      placement="top"
                    >
                      <Button
                        variant="subtle"
                        tone={getButtonTone(option.standard_mapping)}
                        size="sm"
                        icon={<Icon as={style.icon} boxSize="16px" />}
                        onClick={() => onAction(option.standard_mapping)}
                        isDisabled={isLoading}
                        aria-label={option.name}
                      />
                    </Tooltip>
                  );
                })}
                {/* Raise a query for the whole selection — a question, sits
                    alongside the outcome icons (NOT a data fix). */}
                {onRaiseQuery && (
                  <Tooltip
                    label={
                      entitlementOnly
                        ? "Raise a query for the selected entitlements"
                        : "Raise a query for the selected accounts"
                    }
                    hasArrow
                    bg="text.body.primary"
                    color="bg.container"
                    borderColor="border.strong"
                    borderWidth="1px"
                    borderRadius="component.md"
                    px="xs"
                    py="xs"
                    fontSize="14px"
                    fontFamily="body"
                    fontWeight="500"
                    lineHeight="100%"
                    placement="top"
                  >
                    <Button
                      variant="subtle"
                      tone="neutral"
                      size="sm"
                      icon={<Icon as={HelpCircle} boxSize="16px" />}
                      onClick={onRaiseQuery}
                      isDisabled={isLoading}
                      aria-label="Raise a query"
                    />
                  </Tooltip>
                )}
              </HStack>
            </HStack>
          </Flex>
        </Box>
      </Fade>
    );
  },
);

export default FloatingActionBar;
